import React, { useState, useEffect, useCallback, useContext} from 'react';
import AuthContext from '../context/AuthContext';
import Modal from '../components/Modal';
import KendaraanForm from '../components/KendaraanForm';
import ImageViewerModal from '../components/ImageViewerModal';
import Pagination from '../components/Pagination';
import api from '../api/axiosConfig';
import { FaEye } from 'react-icons/fa';
import { showSuccessToast, showErrorAlert, showConfirmDialog } from '../utils/alert';
import './ServicePage.css';

const KendaraanPage = () => {
  const { user } = useContext(AuthContext);
  const [kendaraanList, setKendaraanList] = useState([]);
  const [isFormModalOpen, setFormModalOpen] = useState(false);
  const [isViewerModalOpen, setViewerModalOpen] = useState(false);
  const [imagesToView, setImagesToView] = useState([]);
  const [currentData, setCurrentData] = useState(null);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const API_URL = '/api/kendaraan';

  const fetchKendaraan = useCallback(async (page = currentPage, limit = itemsPerPage, keyword = debouncedSearchTerm) => {
    try {
      const response = await api.get(`${API_URL}?keyword=${keyword}&page=${page}&limit=${limit}`);
      setKendaraanList(response.data.data);
      setTotalItems(response.data.totalItems);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
    } catch (error) { 
      console.error("Gagal mengambil data kendaraan:", error);
      showErrorAlert("Gagal memuat data kendaraan.");
    }
  }, [currentPage, itemsPerPage, debouncedSearchTerm]);

  // useEffect untuk debouncing (tidak berubah)
  useEffect(() => {
    const timerId = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm]);

  useEffect(() => {
    fetchKendaraan();
  }, [debouncedSearchTerm, currentPage, itemsPerPage, fetchKendaraan]);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
    setCurrentPage(1);
  };

  const handleSave = async (formData) => {
    const isEditing = !!currentData;
    const url = isEditing ? `${API_URL}/${currentData._id}` : API_URL;
    const method = isEditing ? 'put' : 'post';
    try {
      await api[method](url, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFormModalOpen(false);
      fetchKendaraan();
      showSuccessToast(isEditing ? 'Data berhasil diperbarui!' : 'Data berhasil ditambahkan!');
    } catch (error) { 
      console.error("Gagal menyimpan data:", error);
      showErrorAlert("Gagal menyimpan data.");
    }
  };

  const handleDelete = async (id) => {
    const result = await showConfirmDialog();
    if (result.isConfirmed) {
      try {
        await api.delete(`${API_URL}/${id}`);
        // 4. Panggil fetchKendaraan dengan term pencarian saat ini
        fetchKendaraan();
        showSuccessToast('Data berhasil dihapus!');
      } catch (error) { 
        console.error("Gagal menghapus data:", error);
        showErrorAlert("Gagal menghapus data.");
      }
    }
  };

  const openFormModal = (data = null) => {
    setCurrentData(data);
    setFormModalOpen(true);
  };
  
  const openViewerModal = (images) => {
    setImagesToView(images);
    setViewerModalOpen(true);
  };
  
  const formatMonthYear = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('id-ID', { month: 'long', year: 'numeric' });
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <p>Master Data / Kendaraan</p>
        <h1>Data Kendaraan</h1>
      </div>
      <div className="content-card">
        <div className="toolbar">
          {(user.role === 'superadmin' || user.role === 'operator') && (
            <button className="btn-primary" onClick={() => openFormModal()}>+ Tambah Kendaraan</button>
          )}
          <input 
            type="text"
            placeholder="Cari Nomor, Jenis, Spesifikasi..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginLeft: 'auto' }}
          />
        </div>
        <div className="table-responsive-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                <th>No.</th>
                <th>Nomor Kendaraan</th>
                <th>Jenis</th>
                <th>Spesifikasi</th>
                <th>Tahun</th>
                <th>Pajak</th>
                <th>Tanggal Pajak</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {kendaraanList.map((item, index) => (
                <tr key={item._id}>
                  <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                  <td>{item.nomorKendaraan}</td>
                  <td>{item.jenis}</td>
                  <td>{item.spesifikasi}</td>
                  <td>{formatMonthYear(item.tahun)}</td>
                  <td>{new Intl.NumberFormat('id-ID', { 
                      style: 'currency', 
                      currency: 'IDR',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0
                  }).format(item.pajak)}</td>
                  <td>{new Date(item.tanggalPajak).toLocaleDateString('id-ID')}</td>
                  {(user.role === 'superadmin' || user.role === 'operator') && (
                    <td className="action-buttons">
                      {item.gambar && item.gambar.length > 0 && (
                        <button onClick={() => openViewerModal(item.gambar)} title="Lihat Gambar">
                          <FaEye />
                        </button>
                      )}
                      {(user.role === 'superadmin' || user.role === 'operator') && (
                        <button onClick={() => openFormModal(item)} title="Edit">✏️</button>
                      )}
                      {(user.role === 'superadmin') && (
                        <button onClick={() => handleDelete(item._id)} title="Hapus">🗑️</button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          itemsPerPage={itemsPerPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
        
      </div>
      
      <Modal show={isFormModalOpen} onClose={() => setFormModalOpen(false)} title={currentData ? "Edit Kendaraan" : "Tambah Kendaraan"}>
        <KendaraanForm
          existingData={currentData}
          onSave={handleSave}
          onCancel={() => setFormModalOpen(false)}
        />
      </Modal>
      <ImageViewerModal
        show={isViewerModalOpen}
        onClose={() => setViewerModalOpen(false)}
        images={imagesToView}
      />
    </div>
  );
};

export default KendaraanPage;